import { _ } from "../../../../core/polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import AlertCollector from "../../../../core/alerts/AlertCollector.js";
import EmailContent from "../../../../models/EmailContent.js";
import EmailNotificationWidget from "../../../reports/periodReport/EmailNotificationWidget/EmailNotificationWidget.js";
import notifier from "../../../../core/widgets/notifier/notifier.js";
import selection from "../../../../models/accounting/document/selection.js";
import session from "../../../../core/session.js";

/**
 * `NotifyUsersDialog` is used within document notification checklist.
 * It displays a popup asking the user to select recipients for sending
 * mail notification for the current selection of items, and give the
 * opportunity to edit the email subject and body.
 *
 * It should only be used through the proper command.
 *
 * @param {Command} command - Command responsible for document
 * notificaion.
 */
export default class NotifyUsersDialog extends Widget {
	constructor(command) {
		super(...arguments);

		this._command = command;
		this._selectedEntries = selection.entries();
		this._currentCompany = session.getCompany();
		this._documentService = command.getDocumentService();
		this._notifiedFolder = this._documentService.getFolderForNotification();
	}

	show() {
		this._documentService.getDocumentNotificationSettings((settings) => {
			this._setupNotificationContent(settings);
			this._showEmailNotificationDialog();
		});
	}

	_setupNotificationContent(settings) {
		this._makeEmailContent(settings);

		const companyUsers = this._filterSelectionsAccessUsers(
			settings.getCompanyUsers(),
		);

		const agencyUsers = this._filterSelectionsAccessUsers(
			settings.getAgencyUsers(),
		);

		this._validationResultCollector = new AlertCollector();

		this._emailNotificationWidget = new EmailNotificationWidget({
			currentCompany: session.getCompany(),
			emailContent: this._emailContent,
			companyUsers,
			agencyUsers,
			submitCallback: () => this._onDialogConfirm(),
		});
	}

	_makeEmailContent(settings) {
		this._emailContent = new EmailContent({
			subject: settings.getNotificationSubject(),
			body: settings.getNotificationBody(),
			sender: session.getCurrentUser(),
		});
	}

	_filterSelectionsAccessUsers(users) {
		const entries = [...this._selectedEntries, this._notifiedFolder];

		return users.filter(
			(user) =>
				user.getId() !== session.getCurrentUser().getId() &&
				user.hasValidEmail() &&
				user.canBeNotifiedForEntries(entries, this._currentCompany),
		);
	}

	_showEmailNotificationDialog() {
		this._confirm({
			title: _("Notify users"),
			body: this._emailNotificationWidget,
			contentCssClass: "resizable-modal-content",
			callback: () => this._emailNotificationWidget.submit(),
		});
	}

	_onDialogConfirm() {
		this._command.setUsers(this._emailContent.getRecipients());

		this._validate({
			onSuccess: () => {
				this._command.setUsers(this._emailContent.getRecipients());

				if (this._emailContent.wasEdited()) {
					this._command.setUserSelectedNotificationSettings(
						this._emailContent.getSubject(),
						this._emailContent.getBody(),
					);
				}

				this._command.doPerform();
			},
			onError: (errors) => this._notifyErrors(errors),
		});
	}

	_validate({ onSuccess, onError }) {
		this._validationResultCollector.reset();

		this._emailContent.validate({
			collector: this._validationResultCollector,
		});

		const errors = this._validationResultCollector.getErrors();
		errors.length ? onError(errors) : onSuccess();
	}

	_notifyErrors(errors) {
		errors.forEach((error) => notifier.error(error.getDescription()));
	}
}
